// src/components/CitySwitcher.js
import React, { useState } from "react";
import { getCurrentUser } from "../services/authService";
import { addCity } from "../services/api";
import { useCity } from "./CityContext";

const CITY_ID_PATTERN = /^[a-z0-9_-]{2,30}$/;

const CitySwitcher = ({ onCityChange }) => {
  const { currentCity, cities, changeCity, refreshCities } = useCity();
  const user = getCurrentUser();

  const [newCityId, setNewCityId] = useState("");
  const [newCityName, setNewCityName] = useState("");
  const [addError, setAddError] = useState(null);
  const [adding, setAdding] = useState(false);

  // Определяем, какие города доступны пользователю:
  // если username совпадает с id какого-то города — доступен только он.
  // Иначе (admin, moderator и т.п.) — доступны все города.
  let allowedCities = cities.map((c) => c.id);

  if (user && cities.some((c) => c.id === user.username)) {
    allowedCities = [user.username];
  }

  // Если текущий город не в разрешённых — переключаем на первый доступный
  React.useEffect(() => {
    if (allowedCities.length > 0 && !allowedCities.includes(currentCity)) {
      const firstAllowed = allowedCities[0];
      changeCity(firstAllowed);
      if (onCityChange) onCityChange(firstAllowed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCity, cities]);

  const handleCityChange = (city) => {
    if (allowedCities.includes(city)) {
      changeCity(city);
      if (onCityChange) onCityChange(city);
    }
  };

  const handleAddCity = async (e) => {
    e.preventDefault();
    setAddError(null);

    const id = newCityId.toLowerCase().trim();
    if (!CITY_ID_PATTERN.test(id)) {
      setAddError("ID города: только строчные латинские буквы, цифры, - и _, от 2 до 30 символов");
      return;
    }
    if (!newCityName.trim()) {
      setAddError("Укажите название города");
      return;
    }

    setAdding(true);
    try {
      await addCity(id, newCityName.trim());
      setNewCityId("");
      setNewCityName("");
      await refreshCities();
    } catch (error) {
      setAddError(error.message || "Не удалось добавить город");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      style={{
        marginBottom: "20px",
        padding: "15px",
        color: "var(--text-primary)",
        border: "1px solid var(--border-color)",
        borderRadius: "8px",
      }}
    >
      <h3 className="sity-vibor">🌍 Выбор города</h3>
      <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
        {cities
          .filter((c) => allowedCities.includes(c.id))
          .map((c) => (
            <button
              key={c.id}
              className={`global-sity-buttons ${currentCity === c.id ? "active" : ""}`}
              onClick={() => handleCityChange(c.id)}
            >
              📍 {c.displayName}
            </button>
          ))}

        <span
          style={{
            marginLeft: "10px",
            color: "var(--text-primary)",
            fontSize: "0.9rem",
          }}
        >
          Текущий: <strong>{cities.find((c) => c.id === currentCity)?.displayName || currentCity}</strong>
        </span>
      </div>

      {user?.role === "admin" && (
        <form
          onSubmit={handleAddCity}
          style={{
            marginTop: "15px",
            paddingTop: "15px",
            borderTop: "1px solid var(--border-color)",
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            placeholder="id (например chelyabinsk)"
            value={newCityId}
            onChange={(e) => setNewCityId(e.target.value)}
            style={{ padding: "6px 10px" }}
          />
          <input
            type="text"
            placeholder="Название (например Челябинск)"
            value={newCityName}
            onChange={(e) => setNewCityName(e.target.value)}
            style={{ padding: "6px 10px" }}
          />
          <button type="submit" className="global-sity-buttons" disabled={adding}>
            {adding ? "Добавление..." : "➕ Добавить город"}
          </button>
          {addError && <span style={{ color: "red", fontSize: "0.85rem" }}>{addError}</span>}
        </form>
      )}
    </div>
  );
};

export default CitySwitcher;
